import React, { useState } from 'react';
import { IconButton, Stack, Tooltip } from '@mui/material';
import { Download, FindInPage, Landscape } from '@mui/icons-material';
import { DocumentPreviewDialog } from '../Document/DocumentAssistant/DocumentPreviewDialog';
import { IDocument } from '../Interfaces/IDocument';
import { customFetcher } from '../hooks/useFetch';
import { useDownloadDocument } from '../hooks/reactQuerys/DocumentQueries';
import { useAlert } from '../context/AlertContext';
import { GrundbuchAuszugImportDialog } from './GrundbuchAuszugImportDialog';
import { useGetKiStatus } from '../hooks/reactQuerys/KiQueries';
import { kiReadsDocuments } from '../Interfaces/IKi';

interface IProps {
  idDocument: number;
  /** Nur für die Fehlermeldung — der Dateiname kommt sonst aus dem Dokument. */
  fileName?: string | null;
}

/**
 * Vorschau und Download eines Grundbuchauszugs direkt in der Abrufliste am
 * Blatt.
 *
 * Bewusst nicht ``DocumentRow``: dort liegt der Download in einem Menü neben
 * „Anzahl Downloads" — eine Zahl, die hier nichts aussagt, weil ein Abruf nie
 * an Mandanten herausgeht. Hier stehen beide Aktionen nebeneinander.
 *
 * Das Dokument wird erst beim Klick geladen (``/document/<id>``); die Liste
 * selbst führt nur ``idDocument`` und Dateiname, nicht die PDF-Daten.
 *
 * Dritte Aktion: den Auszug als Grundbuchblatt übernehmen. Das ist der Weg,
 * auf dem die meisten Auszüge tatsächlich ankommen — im Posteingang landen sie
 * selten, weil sie über den Abruf ins Haus kommen. Der Knopf erscheint nur,
 * wenn die KI eingerichtet und für den Benutzer freigeschaltet ist.
 */
export const AbrufDocumentActions: React.FC<IProps> = ({
  idDocument,
  fileName,
}) => {
  const { showAlert } = useAlert();
  const { downloadDocument, isPending } = useDownloadDocument();
  const [previewDocument, setPreviewDocument] = useState<IDocument | null>(
    null
  );
  const [isPreviewOpen, setIsPreviewOpen] = useState(false);
  const [isLoadingPreview, setIsLoadingPreview] = useState(false);
  const [importFile, setImportFile] = useState<File | null>(null);
  const [isImportOpen, setIsImportOpen] = useState(false);
  const { kiStatus } = useGetKiStatus();

  const label = fileName ?? `Dokument ${idDocument}`;

  const handlePreview = async () => {
    setIsPreviewOpen(true);
    if (previewDocument || isLoadingPreview) return;
    setIsLoadingPreview(true);
    try {
      const loaded: IDocument = await customFetcher({
        endpoint: `/document/${idDocument}`,
      });
      setPreviewDocument(loaded);
    } catch {
      setIsPreviewOpen(false);
      showAlert({
        severity: 'error',
        text: `${label} konnte nicht geöffnet werden.`,
      });
    } finally {
      setIsLoadingPreview(false);
    }
  };

  const handleImport = async () => {
    setIsImportOpen(true);

    if (importFile !== null) {
      return;
    }

    try {
      // Dieselbe Quelle wie die Vorschau. `Data` ist Base64 und liegt erst
      // nach diesem Aufruf vor — die Abrufliste führt es nicht mit.
      const loaded: IDocument = await customFetcher({
        endpoint: `/document/${idDocument}`,
      });
      const binary = atob(loaded.Data ?? '');
      const bytes = new Uint8Array(binary.length);

      for (let index = 0; index < binary.length; index += 1) {
        bytes[index] = binary.charCodeAt(index);
      }

      setImportFile(
        new File([bytes], loaded.FileName || label, {
          type: 'application/pdf',
        })
      );
    } catch {
      setIsImportOpen(false);
      showAlert({
        severity: 'error',
        text: `${label} konnte nicht geladen werden.`,
      });
    }
  };

  const handleDownload = async () => {
    try {
      await downloadDocument(idDocument);
    } catch {
      showAlert({
        severity: 'error',
        text: `${label} konnte nicht heruntergeladen werden.`,
      });
    }
  };

  return (
    <>
      <DocumentPreviewDialog
        open={isPreviewOpen}
        localDocumentObject={previewDocument}
        onClose={() => setIsPreviewOpen(false)}
      />
      {isImportOpen && (
        <GrundbuchAuszugImportDialog
          open={isImportOpen}
          onClose={() => setIsImportOpen(false)}
          initialFile={importFile}
        />
      )}
      <Stack direction='row' spacing={0.5}>
        <Tooltip title='Vorschau'>
          <IconButton size='small' onClick={() => void handlePreview()}>
            <FindInPage fontSize='small' />
          </IconButton>
        </Tooltip>
        {kiReadsDocuments(kiStatus) && (
          <Tooltip title='Als Grundbuchblatt übernehmen'>
            <IconButton size='small' onClick={() => void handleImport()}>
              <Landscape fontSize='small' />
            </IconButton>
          </Tooltip>
        )}
        <Tooltip title='Herunterladen'>
          <span>
            <IconButton
              size='small'
              disabled={isPending}
              onClick={() => void handleDownload()}
            >
              <Download fontSize='small' />
            </IconButton>
          </span>
        </Tooltip>
      </Stack>
    </>
  );
};
